import { ColumnType } from './column-type.enum';

export type OnlyIfPredicateFn = () => boolean;

export interface ColumnConfig {
  cellClasses?: string[];
}

export abstract class Column<T> {
  /** The column title, to be displayed once along the top of the table */
  public readonly title: string;

  /** The type of column this is */
  public abstract columnType: ColumnType;

  /** The column's configuration */
  public abstract config: ColumnConfig;

  /** Whether this column should be displayed in the view. If it's false, this column should be skipped like it doesn't exist. */
  public visible = true;

  /**
   * This private variable ensures <T> is bound.
   * It means a list of Column<Foo> is implied to contain Foo columns and no other type of column.
   * @internal
   */
  protected readonly content_: T;

  /**
   * Whether to show this column in the table. This is checked only once, and only at definition time.
   *
   * @example new TextColumn(...).onlyIf(() => true); // this column will show up like normal
   * @example new TextColumn(...).onlyIf(() => false); // this column will not show up in the table
   *
   * @param predicate A function returning true/false that determines whether this column should be included.
   */
  public onlyIf(predicate: OnlyIfPredicateFn): this {
    this.visible = !!predicate();
    return this;
  }

  public toString(): string {
    return `[Column (type: ${this.columnType}, title: ${this.title})]`;
  }

  /**
   * Classes to apply to each cell in the head or body of this column.
   */
  public get cellClasses(): string {
    const styles = this.config.cellClasses ?? [];
    return styles.join(' ');
  }

  protected applyConfig(extraConfig: ColumnConfig) {
    if (extraConfig) {
      Object.keys(extraConfig).forEach(key => {
        this.config[key] = extraConfig[key];
      });
    }
  }
}
